import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AvatarPerson } from '../../../core/components/Avatar.tsx';
import Button from '../../../core/components/Button.tsx';
import EmptyStage from '../../../core/components/EmptyStage.tsx';
import FilterDropdown from '../../../core/components/Filter.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import SearchField from '../../../core/components/SearchField.tsx';
import { SAD } from '../../../core/components/worki/Pose.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import useSearchParamState, { useArraySearchParamState } from '../../../core/utils/useSearchParamState';
import { canManageAgents, canManageVoiceAgents, canSeeAgents, canSeeVoiceAgents } from '../../../users/utils.ts';
import { GET_VOICE_AGENTS } from '../../voice_agents/queries.ts';
import AssistantCard from '../components/AssistantCard.tsx';
import CreateAssistantWizard from '../components/CreateAssistantWizard.tsx';
import LockedAssistantCard from '../components/LockedAssistantCard.tsx';
import { GET_AGENTS, GET_PERSONAL_MAILBOX_OWNERS } from '../queries.ts';
import { adaptAgent, adaptVoiceAgent, AssistantCardData } from '../utils/adaptAssistant.ts';

const AddIcon = withIcon(faPlus);

const OverviewPage = () => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { t: tVoiceAgents } = useTranslation('voice_agents');
    const { user } = useApplicationContext();
    const navigate = useNavigate();

    const canSeeChatAgents = canSeeAgents(user);
    const canSeeCallAgents = canSeeVoiceAgents(user);

    const {
        data: agentsData,
        loading: agentsLoading,
        refetch: refetchAgents,
    } = useQuery(GET_AGENTS, {
        fetchPolicy: 'network-only',
        skip: !canSeeChatAgents,
    });
    const {
        data: voiceAgentsData,
        loading: voiceAgentsLoading,
        refetch: refetchVoiceAgents,
    } = useQuery(GET_VOICE_AGENTS, {
        fetchPolicy: 'network-only',
        skip: !canSeeCallAgents,
    });

    const refetchAll = () => {
        if (canSeeChatAgents) refetchAgents();
        if (canSeeCallAgents) refetchVoiceAgents();
    };

    const { data: personalMailboxesData } = useQuery(GET_PERSONAL_MAILBOX_OWNERS, {
        fetchPolicy: 'cache-and-network',
        skip: !canSeeChatAgents,
    });
    const personalMailboxOwners = useMemo(
        () =>
            new Map<string, AvatarPerson>(
                (personalMailboxesData?.personalSmartInboxIntegrations || []).map((mailbox: any) => [
                    mailbox.id,
                    mailbox.createdBy,
                ])
            ),
        [personalMailboxesData]
    );

    const canCreateAssistant = canManageAgents(user) || canManageVoiceAgents(user);
    const [isWizardOpen, setIsWizardOpen] = useState(false);

    const defaultTeamLabel = user?.customer?.name || t('overview.defaultTeam');

    const assistants: AssistantCardData[] = useMemo(() => {
        const agents = (agentsData?.agents || []).map((agent: any) =>
            adaptAgent(agent, tAgentConfig, t('overview.masterDataShort'), defaultTeamLabel, personalMailboxOwners)
        );
        const voiceAgents = (voiceAgentsData?.voiceAgents || []).map((voiceAgent: any) =>
            adaptVoiceAgent(voiceAgent, tVoiceAgents, defaultTeamLabel)
        );
        return [...agents, ...voiceAgents];
    }, [agentsData, voiceAgentsData, tAgentConfig, tVoiceAgents, t, defaultTeamLabel, personalMailboxOwners]);

    const loading = (canSeeChatAgents && agentsLoading) || (canSeeCallAgents && voiceAgentsLoading);
    const hasAssistants = assistants.length > 0;
    const isEmpty = !loading && !hasAssistants && canSeeChatAgents && canSeeCallAgents;

    const [search, setSearch] = useSearchParamState('search', '');
    const [typeFilter, setTypeFilter] = useArraySearchParamState('type');
    const showTypeFilter = canSeeChatAgents && canSeeCallAgents;
    const hasActiveFilters = !!search || typeFilter.length > 0;
    const clearFilters = () => {
        setSearch('');
        setTypeFilter([]);
    };

    const needle = search.trim().toLowerCase();
    const visibleAssistants = assistants.filter(
        (assistant) =>
            (typeFilter.length === 0 || typeFilter.includes(assistant.kind)) &&
            (!needle ||
                assistant.name.toLowerCase().includes(needle) ||
                assistant.subtitle?.toLowerCase().includes(needle))
    );

    return (
        <Layout>
            <Page>
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('overview.title')}</Page.HeaderTitle>
                    {canCreateAssistant && !isEmpty && (
                        <Page.HeaderButton onClick={() => setIsWizardOpen(true)}>
                            <AddIcon />
                            {t('overview.addAssistant')}
                        </Page.HeaderButton>
                    )}
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-4">
                    {isEmpty ? (
                        <EmptyStage
                            size={88}
                            title={t('overview.emptyTitle')}
                            description={t('overview.emptyDescription')}
                            action={
                                canCreateAssistant && (
                                    <Button variant="primary" onClick={() => setIsWizardOpen(true)}>
                                        {t('overview.addAssistant')}
                                    </Button>
                                )
                            }
                        />
                    ) : (
                        <>
                            {hasAssistants && (
                                <div className="-mt-3 flex items-center justify-end gap-2">
                                    <SearchField
                                        placeholder={t('overview.searchPlaceholder')}
                                        value={search}
                                        onValueChange={setSearch}
                                    />
                                    {showTypeFilter && (
                                        <FilterDropdown
                                            variant="ghost"
                                            label={t('overview.filters.type.label')}
                                            allLabel={t('overview.filters.type.all')}
                                            options={[
                                                { value: 'inboxAgent', label: t('overview.filters.type.inboxAgent') },
                                                { value: 'voiceAgent', label: t('overview.filters.type.voiceAgent') },
                                            ]}
                                            selected={typeFilter}
                                            onSelectedChange={setTypeFilter}
                                            multiple
                                        />
                                    )}
                                </div>
                            )}
                            {hasAssistants && visibleAssistants.length === 0 ? (
                                <EmptyStage
                                    size={88}
                                    pose={SAD}
                                    title={t('overview.noResultsTitle')}
                                    description={t('overview.noResultsDescription')}
                                    action={
                                        <Button variant="outline" onClick={clearFilters}>
                                            {t('overview.clearFilters')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <div className="grid grid-cols-[repeat(auto-fill,minmax(0,380px))] gap-4">
                                    {visibleAssistants.map((assistant) => (
                                        <AssistantCard
                                            key={`${assistant.kind}-${assistant.id}`}
                                            assistant={assistant}
                                            onChanged={refetchAll}
                                        />
                                    ))}
                                    {!hasActiveFilters && !canSeeCallAgents && (
                                        <LockedAssistantCard kind="voiceAgent" />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {isWizardOpen && (
                        <CreateAssistantWizard onClose={() => setIsWizardOpen(false)} onCreated={navigate} />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OverviewPage;
